import type { ApolloError } from '@apollo/client'
import { captureException, getCurrentScope } from '@sentry/capacitor'
import { AuthError } from 'firebase/auth'
export type Metadata = {
  error?: Error
  userMessage?: String
  [key: string]: unknown
}

export const errors = {
  noImplemented(message: string, metadata?: Metadata) {
    return new (class NoImplementedError extends Error {
      constructor(message: string, metadata?: Metadata) {
        super(message)
      }
    })(message, metadata)
  },
}

type ErrorObj =
  | Error
  | THEOplayer.ErrorEvent
  | AuthError
  | ApolloError
  | [event: THEOplayer.ContentProtectionErrorEvent]

// toError が構築した Error に元の型名を残す非列挙プロパティのキー。captureError が Sentry の extra に乗せる。
const TO_ERROR_ORIGINAL_TYPE_KEY = '__toErrorOriginalType' as const

/**
 * Sentry の captureException でエラー出力を行う。
 * @param error エラーオブジェクト
 * @param occured どこでエラー出力が行われたかを示す文字列
 *               (スペルミスだが Sentry 側の既存タグキー / アラート設定との互換のため "occured" のまま使用)
 * @param extra Sentryへ残す付随情報
 */
export function captureError(
  error: ErrorObj,
  occured: string,
  extra?: Record<string, unknown>
) {
  const scope = getCurrentScope()
  scope?.setTag('occured', occured)
  const originalType = (error as { [TO_ERROR_ORIGINAL_TYPE_KEY]?: string })?.[
    TO_ERROR_ORIGINAL_TYPE_KEY
  ]
  if (originalType !== undefined) {
    scope?.setExtra('toErrorOriginalType', originalType)
  }
  captureException(error, { extra })
  return null
}

/**
 * `catch (e: unknown)` で受けた値を Error 型に正規化する。`showError` などの `Error | string` を受ける関数に安全に渡せる。
 *
 * - Error 派生 (ApolloError 等含む): そのまま返す
 * - string: new Error(message) でラップ
 * - null / undefined: 'Unknown error' メッセージで明示
 * - number / boolean / bigint / symbol: String() でメッセージ化
 * - object / function: 'Unknown error' を返し、constructor.name のみ非列挙プロパティで添付。
 *   任意の構造体を JSON.stringify するとセンシティブ情報が UI / Sentry に露出する恐れがあるため。
 *   function を String() すると関数ソースコードが漏れるため、こちらの経路で扱う。
 */
export function toError(value: unknown): Error {
  if (value instanceof Error) return value
  if (typeof value === 'string') return new Error(value)
  if (value === null || value === undefined) return new Error('Unknown error')
  if (typeof value !== 'object' && typeof value !== 'function') {
    return new Error(String(value))
  }
  const err = new Error('Unknown error')
  // null prototype オブジェクトでは constructor が undefined になりうるため `?.` で吸収する。
  // フォールバックは 'unknown' (`{}` の 'Object' (大文字) と混ぜると Sentry の検索性が悪くなるため小文字)。
  const originalType =
    (value as { constructor?: { name?: string } }).constructor?.name ??
    'unknown'
  Object.defineProperty(err, TO_ERROR_ORIGINAL_TYPE_KEY, {
    value: originalType,
    enumerable: false,
    writable: false,
    configurable: true,
  })
  return err
}
